import axios from "axios";
import moment from "moment";
import "moment/locale/es";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

moment.locale("es");

const MODULO = "InformeContractual";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const ListarInformesContractuales = () => {
  const { id } = useParams();

  const [informes, setInformes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const navigate = useNavigate();

  // =========================================
  // AUTH CONFIG
  // =========================================

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // =========================================
  // PERMISOS
  // =========================================

  const getModuloInforme = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
          ? JSON.parse(usuarioStorage)
          : userStorage
            ? JSON.parse(userStorage)
            : usuariosStorage
              ? JSON.parse(usuariosStorage)
              : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
        permisosData?.permisos ||
        permisosData?.usuario?.modulos ||
        permisosData?.usuario?.permisos ||
        permisosData?.user?.modulos ||
        permisosData?.user?.permisos ||
        [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo ||
            modulo?.modulo ||
            modulo?.nombre ||
            "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloInforme = useMemo(() => getModuloInforme(), []);

  const puedeCrear =
    moduloInforme?.puedeCrear === true ||
    moduloInforme?.crear === true ||
    moduloInforme?.create === true;

  const puedeActualizar =
    moduloInforme?.puedeActualizar === true ||
    moduloInforme?.actualizar === true ||
    moduloInforme?.editar === true ||
    moduloInforme?.update === true;

  const puedeEliminar =
    moduloInforme?.puedeEliminar === true ||
    moduloInforme?.eliminar === true ||
    moduloInforme?.delete === true;

  // =========================================
  // VALIDAR SESIÓN
  // =========================================

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/login");
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  // =========================================
  // GENERAR WORD
  // =========================================

  const generarYDescargarInforme = useCallback(
    async (
      idInforme,
      documentoInstructor,
      rp,
      fechaInicio,
      mes
    ) => {
      try {
        setDownloadingId(idInforme);

        const response = await axios.get(
          `/api/informe-contractual/generar-docx/${idInforme}`,
          {
            ...authConfig(),
            responseType: "blob",
          }
        );

        const anioInicio = fechaInicio
          ? moment(fechaInicio).format("YYYY")
          : "N-A";

        const mesFormatted = mes
          ? String(mes).toUpperCase()
          : "SIN_MES";

        const rpValue = rp !== "N/A" ? rp : "SINRP";

        const docInstr =
          documentoInstructor !== "N/A"
            ? documentoInstructor
            : "SIN_DOC";

        const fileName = `GC_${docInstr}_${rpValue}_${mesFormatted}_${anioInicio}.docx`;

        const url = window.URL.createObjectURL(
          new Blob([response.data])
        );

        const link = document.createElement("a");

        link.href = url;

        link.setAttribute("download", fileName);

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error al generar informe:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire(
          "Error",
          error.response?.data?.message ||
          "No se pudo generar el informe.",
          "error"
        );
      } finally {
        setDownloadingId(null);
      }
    },
    [manejarErrorSesion]
  );

  // =========================================
  // DUPLICAR
  // =========================================

  const duplicarInforme = useCallback(
    async (informeId) => {
      if (!puedeCrear) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para duplicar informes contractuales.",
          "warning"
        );

        return;
      }

      try {
        const result = await Swal.fire({
          title: "¿Desea duplicar este informe?",
          text: "Se creará una copia del informe seleccionado.",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Sí, duplicar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return;

        setDuplicatingId(informeId);

        const { data } = await axios.post(
          `/api/informecontractual/duplicar/${informeId}`,
          {},
          authConfig()
        );

        await getData();

        Swal.fire({
          icon: "success",
          title:
            data.message ||
            "Informe duplicado correctamente",
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (error) {
        console.error("Error al duplicar:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire(
          "Error",
          error.response?.data?.message ||
          "No se pudo duplicar el informe.",
          "error"
        );
      } finally {
        setDuplicatingId(null);
      }
    },
    [puedeCrear, manejarErrorSesion]
  );

  // =========================================
  // ELIMINAR
  // =========================================

  const deleteInforme = useCallback(
    async (informeId) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar informes contractuales.",
          "warning"
        );

        return;
      }

      try {
        const result = await Swal.fire({
          title: "¿Está seguro?",
          text: "¡Esta acción eliminará el informe!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return;

        setDeletingId(informeId);

        const { data } = await axios.delete(
          `/api/informecontractual/delete/${informeId}`,
          authConfig()
        );

        setInformes((prev) =>
          prev.filter((i) => i._id !== informeId)
        );

        Swal.fire({
          icon: "success",
          title: data.message || "Informe eliminado",
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (error) {
        console.error("Error al eliminar:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire(
          "Error",
          error.response?.data?.message ||
          "No se pudo eliminar el informe.",
          "error"
        );
      } finally {
        setDeletingId(null);
      }
    },
    [puedeEliminar, manejarErrorSesion]
  );

  // =========================================
  // OBTENER DATOS
  // =========================================

  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `/api/informecontractual/listbycontrato/${id}`,
        authConfig()
      );

      let data = response.data?.informes || [];

      data.sort(
        (a, b) =>
          new Date(a.fechaInforme) -
          new Date(b.fechaInforme)
      );

      const dataConItem = data.map((informe, index) => ({
        ...informe,
        item: index + 1,
      }));

      const informesConFunciones = dataConItem
        .reverse()
        .map((informe) => {
          const documentoInstructor =
            informe.documentoInstructor ?? "N/A";

          const rp = informe.rp ?? "N/A";

          const fechaInicio =
            informe.fechaInicio ?? null;

          const mes = informe.mes ?? null;

          return {
            ...informe,

            fechaInforme: informe.fechaInforme
              ? moment
                .utc(informe.fechaInforme)
                .add(5, "hours")
                .format("DD/MM/YYYY")
              : "-",

            funciones: (
              <>
                {/* ESCRITORIO - ORIGINAL */}
                <div className="d-none d-md-flex gap-2 flex-wrap justify-content-end">
                  <button
                    className="btn btn-dark btn-sm d-inline-flex align-items-center justify-content-center"
                    style={{ width: 34, height: 34 }}
                    onClick={() =>
                      generarYDescargarInforme(
                        informe._id,
                        documentoInstructor,
                        rp,
                        fechaInicio,
                        mes
                      )
                    }
                    title="Descargar Informe"
                    type="button"
                    disabled={loading || downloadingId === informe._id}
                  >
                    {downloadingId === informe._id ? (
                      <i className="fa-solid fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fa-solid fa-file-word"></i>
                    )}
                  </button>

                  {puedeCrear && (
                    <button
                      className="btn btn-outline-primary btn-sm d-inline-flex align-items-center justify-content-center"
                      style={{ width: 34, height: 34 }}
                      disabled={loading || duplicatingId === informe._id}
                      onClick={() => duplicarInforme(informe._id)}
                      title="Duplicar Informe"
                      type="button"
                    >
                      {duplicatingId === informe._id ? (
                        <i className="fa-solid fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fa-solid fa-copy"></i>
                      )}
                    </button>
                  )}

                  {puedeActualizar && (
                    <button
                      className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                      style={{ width: 34, height: 34 }}
                      disabled={loading}
                      onClick={() =>
                        navigate(`/instructor/informecontractual/editar/${informe._id}`)
                      }
                      title="Editar Informe"
                      type="button"
                    >
                      <i className="fa-solid fa-pen-to-square"></i>
                    </button>
                  )}

                  {puedeEliminar && (
                    <button
                      className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                      style={{ width: 34, height: 34 }}
                      disabled={loading || deletingId === informe._id}
                      onClick={() => deleteInforme(informe._id)}
                      title="Eliminar Informe"
                      type="button"
                    >
                      {deletingId === informe._id ? (
                        <i className="fa-solid fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fa-solid fa-trash"></i>
                      )}
                    </button>
                  )}
                </div>

                {/* RESPONSIVE - ICONO ARRIBA / TEXTO ABAJO */}
                <div className="d-flex d-md-none gap-2 flex-wrap justify-content-end">
                  <button
                    className="btn btn-outline-success btn-sm d-flex flex-column align-items-center justify-content-center"
                    style={{ width: "calc(50% - 0.25rem)" }}
                    type="button"
                    title="Descargar Informe"
                    onClick={() =>
                      generarYDescargarInforme(
                        informe._id,
                        documentoInstructor,
                        rp,
                        fechaInicio,
                        mes
                      )
                    }
                    disabled={loading || downloadingId === informe._id}
                  >
                    {downloadingId === informe._id ? (
                      <i className="fa-solid fa-spinner fa-spin mb-1"></i>
                    ) : (
                      <i className="fa-solid fa-file-word mb-1"></i>
                    )}
                    <small>Word</small>
                  </button>

                  {puedeCrear && (
                    <button
                      className="btn btn-outline-primary btn-sm d-flex flex-column align-items-center justify-content-center"
                      style={{ width: "calc(50% - 0.25rem)" }}
                      type="button"
                      title="Duplicar Informe"
                      onClick={() => duplicarInforme(informe._id)}
                      disabled={loading || duplicatingId === informe._id}
                    >
                      {duplicatingId === informe._id ? (
                        <i className="fa-solid fa-spinner fa-spin mb-1"></i>
                      ) : (
                        <i className="fa-solid fa-copy mb-1"></i>
                      )}
                      <small>Duplicar</small>
                    </button>
                  )}

                  {puedeActualizar && (
                    <button
                      className="btn btn-outline-warning btn-sm d-flex flex-column align-items-center justify-content-center"
                      style={{ width: "calc(50% - 0.25rem)" }}
                      type="button"
                      title="Editar Informe"
                      onClick={() =>
                        navigate(
                          `/instructor/informecontractual/editar/${informe._id}`
                        )
                      }
                      disabled={loading}
                    >
                      <i className="fa-solid fa-pen-to-square mb-1"></i>
                      <small>Editar</small>
                    </button>
                  )}

                  {puedeEliminar && (
                    <button
                      className="btn btn-outline-danger btn-sm d-flex flex-column align-items-center justify-content-center"
                      style={{ width: "calc(50% - 0.25rem)" }}
                      type="button"
                      title="Eliminar Informe"
                      onClick={() => deleteInforme(informe._id)}
                      disabled={loading || deletingId === informe._id}
                    >
                      {deletingId === informe._id ? (
                        <i className="fa-solid fa-spinner fa-spin mb-1"></i>
                      ) : (
                        <i className="fa-solid fa-trash mb-1"></i>
                      )}
                      <small>Eliminar</small>
                    </button>
                  )}
                </div>
              </>
            ),
          };
        });

      setInformes(informesConFunciones);
    } catch (error) {
      console.error(
        "Error al obtener informes:",
        error
      );

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message ||
        "No se pudieron cargar los informes.",
        "error"
      );

      setInformes([]);
    } finally {
      setLoading(false);
    }
  }, [
    id,
    puedeCrear,
    puedeActualizar,
    puedeEliminar,
    navigate,
    generarYDescargarInforme,
    duplicarInforme,
    deleteInforme,
    manejarErrorSesion,
  ]);

  useEffect(() => {
    if (id) {
      getData();
    }
  }, [id, getData]);

  // =========================================
  // COLUMNAS
  // =========================================

  const columns = useMemo(
    () => [
      { Header: "Item", accessor: "item" },
      { Header: "Ciudad", accessor: "ciudad" },
      {
        Header: "Fecha Informe",
        accessor: "fechaInforme",
      },
      { Header: "Mes", accessor: "mes" },
      { Header: "Año", accessor: "anio" },
      {
        Header: "N° Planilla",
        accessor: "numeroPlanilla",
      },
      {
        Header: "Periodo",
        accessor: "periodoPlanilla",
      },
      {
        Header: "Operador",
        accessor: "operadorPlanilla",
      },
      {
        Header: "Viajes",
        accessor: "totalDesplazamientos",
      },
      {
        Header: "Acciones",
        accessor: "funciones",
      },
    ],
    []
  );

  // =========================================
  // RENDER
  // =========================================

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">

              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Informes mensuales registrados
                </h5>

                <small className="text-secondary">
                  Descarga, edición y control de informes contractuales.
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">
                <button
                  className="btn btn-outline-dark flex-fill"
                  type="button"
                  onClick={() => navigate(-1)}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary flex-fill"
                  type="button"
                  onClick={getData}
                  title="Actualizar"
                  disabled={loading}
                >
                  <i
                    className={`fa-solid ${loading
                      ? "fa-spinner fa-spin"
                      : "fa-rotate"
                      } me-2`}
                  ></i>

                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark flex-fill"
                    type="button"
                    disabled={loading}
                    onClick={() =>
                      navigate(
                        `/instructor/informecontractual/${id}`
                      )
                    }
                    title="Nuevo Informe"
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* BODY */}

          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">

                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">
                  Cargando informes…
                </div>
              </div>
            ) : Array.isArray(informes) &&
              informes.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable
                  columns={columns}
                  data={informes}
                />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">

                <div className="display-6 mb-2">
                  📄
                </div>

                <div className="fw-semibold">
                  No hay informes registrados
                </div>

                <div className="small mb-3">
                  No se encontraron informes para este contrato.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() =>
                      navigate(
                        `/instructor/informecontractual/${id}`
                      )
                    }
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo Informe
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarInformesContractuales;